import { useEffect, useMemo, useState } from 'react';

import crypto from 'crypto';

import { setImageServiceForRelatedProducts } from '~/grid/utils/cioUtils';
import { useApolloClient } from '~/techstyle-shared/react-graphql';

import { getGqlRelatedProductMap } from './gqlUtils';

export default function useInitialProductRelatedProducts({
  initialProducts,
  useDelayedRelatedProducts = true,
  shouldUseImageService = false,
}) {
  const apolloClient = useApolloClient();
  const [
    initialProductsWithRelatedProducts,
    setInitialProductsWithRelatedProducts,
  ] = useState({});
  const [productRelatedProductsMap, setProductRelatedProductsMap] = useState(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);

  const cacheKey = useMemo(
    () =>
      crypto
        .createHash('sha256')
        .update(
          JSON.stringify({
            products: initialProducts.products,
          })
        )
        .digest('hex'),
    [initialProducts]
  );

  useEffect(() => {
    const getInitialProductRelatedProducts = async () => {
      if (!initialProducts?.products?.length) {
        return;
      }

      setIsLoading(true);

      const gqlRelatedProductsMap = await getGqlRelatedProductMap({
        products: initialProducts.products,
        apolloClient,
      });

      const relatedProductsMap = shouldUseImageService
        ? setImageServiceForRelatedProducts({
            relatedProductsMap: gqlRelatedProductsMap,
          })
        : gqlRelatedProductsMap;

      setProductRelatedProductsMap({
        ...productRelatedProductsMap,
        ...relatedProductsMap,
      });

      const productListingData = {
        ...initialProducts,
        products: initialProducts?.products?.map((product) => {
          return {
            ...product,
            ...productRelatedProductsMap[product.masterProductId],
          };
        }),
      };
      setInitialProductsWithRelatedProducts({
        ...initialProductsWithRelatedProducts,
        [cacheKey]: productListingData,
      });

      setIsLoading(false);
    };

    if (
      useDelayedRelatedProducts &&
      !initialProductsWithRelatedProducts[cacheKey]
    ) {
      getInitialProductRelatedProducts();
    }
  }, [
    cacheKey,
    initialProductsWithRelatedProducts,
    initialProducts,
    apolloClient,
    productRelatedProductsMap,
    useDelayedRelatedProducts,
    shouldUseImageService,
  ]);

  return {
    initialProductsWithRelatedProducts:
      initialProductsWithRelatedProducts[cacheKey],
    productRelatedProductsMap,
    isLoading,
  };
}
